<div class="page-hero">
  <div>
    <p class="eyebrow">Fortune Stats</p>
    <h2>Reporting by machine, location, and sales rep</h2>
    <p class="sub">Use this page to review completed daily stats from the database and optionally blend in current-day live progress for up-to-date reporting.</p>
  </div>
  <div class="hero-actions d-flex gap-2 flex-wrap">
    <a class="btn btn-light shadow-sm" routerLink="/fusion-connect">Back to live monitor</a>
    <button class="btn btn-outline-light shadow-sm" (click)="exportRowsCsv()">Export CSV</button>
    <button class="btn btn-outline-light shadow-sm" (click)="printReport()">Print</button>
  </div>
</div>

<div class="row g-3 mb-4">
  <div class="col-md-3" *ngIf="showOperationalSummaryCards">
    <div class="stat-card shadow-sm">
      <div class="stat-label">Report rows</div>
      <div class="stat-value">{{ rows.length }}</div>
      <div class="stat-help">grouped results currently displayed</div>
    </div>
  </div>
  <div class="col-md-3" *ngIf="showOperationalSummaryCards">
    <div class="stat-card shadow-sm">
      <div class="stat-label">Total reads</div>
      <div class="stat-value">{{ totalReads | number }}</div>
      <div class="stat-help">read events included in this report</div>
    </div>
  </div>
  <div [class]="showOperationalSummaryCards ? 'col-md-3' : 'col-md-6'">
    <div class="stat-card shadow-sm">
      <div class="stat-label">Cash in</div>
      <div class="stat-value">{{ totalCashIn | number:'1.0-0' }}</div>
      <div class="stat-help">summed across the selected period</div>
    </div>
  </div>
  <div [class]="showOperationalSummaryCards ? 'col-md-3' : 'col-md-6'">
    <div class="stat-card shadow-sm">
      <div class="stat-label">Net</div>
      <div class="stat-value">{{ totalNet | number:'1.0-0' }}</div>
      <div class="stat-help">last refresh {{ lastLoadedAt | date:'shortTime' }}</div>
    </div>
  </div>
</div>

<div class="card shadow-sm mb-4">
  <div class="card-header">
    <div class="eyebrow text-uppercase">Filters</div>
    <h5 class="mb-0">Build the stats view you want</h5>
  </div>
  <div class="card-body">
    <form [formGroup]="filters" class="row g-3 align-items-end">
      <div class="col-md-2">
        <label class="form-label">From</label>
        <input
          class="form-control stats-date-input"
          type="text"
          formControlName="from"
          ngbDatepicker
          #fromDatePicker="ngbDatepicker"
          [startDate]="currentMonth"
          (click)="openDatepicker(fromDatePicker, $event)"
        />
      </div>
      <div class="col-md-2">
        <label class="form-label">To</label>
        <input
          class="form-control stats-date-input"
          type="text"
          formControlName="to"
          ngbDatepicker
          #toDatePicker="ngbDatepicker"
          [startDate]="currentMonth"
          (click)="openDatepicker(toDatePicker, $event)"
        />
      </div>
      <div class="col-md-2">
        <label class="form-label">View by</label>
        <select class="form-select" formControlName="groupBy">
          <option value="location">Location</option>
          <option value="salesRep">Sales rep</option>
          <option value="board">Machine</option>
        </select>
      </div>
      <div class="col-md-2">
        <label class="form-label">Period</label>
        <select class="form-select" formControlName="period">
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>
      <div class="col-md-4">
        <label class="form-label">Machine filter</label>
        <select class="form-select" formControlName="boardId">
          <option value="">All boards</option>
          <option *ngFor="let b of boards; trackBy: trackByRow" [value]="b.id">
            {{ b.assetTag || b.serialNumber || b.id }} · {{ b.location?.locationName || b.currentDeployment?.location?.locationName || 'No location' }}
          </option>
        </select>
      </div>
      <div class="col-md-8">
        <div class="form-check mt-2">
          <input class="form-check-input" type="checkbox" formControlName="includeTodayLive" id="includeTodayLive" />
          <label class="form-check-label" for="includeTodayLive">
            Include current-day live progress so today’s numbers stay up to date
          </label>
        </div>
      </div>
      <div class="col-md-4 d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-outline-secondary" (click)="resetFilters()">Reset</button>
        <button type="button" class="btn btn-outline-warning" [disabled]="generating || filters.invalid" (click)="generateStatsFromReads()">{{ generating ? 'Generating…' : 'Create stats' }}</button>
        <button type="button" class="btn btn-primary" [disabled]="loading || filters.invalid" (click)="loadReport()">Apply</button>
      </div>
    </form>
  </div>
</div>

<div class="row g-4 mb-4">
  <div class="col-lg-7">
    <div class="card shadow-sm h-100">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          <div class="eyebrow text-uppercase">Charts</div>
          <h5 class="mb-0">Performance comparison</h5>
        </div>
        <span class="text-muted small">Top groups by current filter</span>
      </div>
      <div class="card-body">
        <apx-chart *ngIf="comparisonChartOptions"
          [series]="comparisonChartOptions.series"
          [chart]="comparisonChartOptions.chart"
          [plotOptions]="comparisonChartOptions.plotOptions"
          [dataLabels]="comparisonChartOptions.dataLabels"
          [stroke]="comparisonChartOptions.stroke"
          [xaxis]="comparisonChartOptions.xaxis"
          [legend]="comparisonChartOptions.legend"
          [colors]="comparisonChartOptions.colors">
        </apx-chart>
      </div>
    </div>
  </div>
  <div class="col-lg-5">
    <div class="card shadow-sm h-100">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          <div class="eyebrow text-uppercase">Reads mix</div>
          <h5 class="mb-0">Distribution by selected grouping</h5>
        </div>
      </div>
      <div class="card-body">
        <apx-chart *ngIf="distributionChartOptions"
          [series]="distributionChartOptions.series"
          [chart]="distributionChartOptions.chart"
          [labels]="distributionChartOptions.labels"
          [legend]="distributionChartOptions.legend"
          [dataLabels]="distributionChartOptions.dataLabels"
          [colors]="distributionChartOptions.colors">
        </apx-chart>
      </div>
    </div>
  </div>
</div>

<div class="card shadow-sm mb-4">
  <div class="card-header d-flex justify-content-between align-items-center">
    <div>
      <div class="eyebrow text-uppercase">Trend</div>
      <h5 class="mb-0">Period trend view</h5>
    </div>
    <span class="text-muted small">Daily, weekly, monthly, or yearly totals</span>
  </div>
  <div class="card-body">
    <apx-chart *ngIf="trendChartOptions"
      [series]="trendChartOptions.series"
      [chart]="trendChartOptions.chart"
      [stroke]="trendChartOptions.stroke"
      [dataLabels]="trendChartOptions.dataLabels"
      [xaxis]="trendChartOptions.xaxis"
      [colors]="trendChartOptions.colors">
    </apx-chart>
  </div>
</div>

<div class="card shadow-sm">
  <div class="card-header d-flex justify-content-between align-items-center">
    <div>
      <div class="eyebrow text-uppercase">Report output</div>
      <h5 class="mb-0">Completed stats + live progress</h5>
    </div>
    <span class="text-muted small">Daily, weekly, monthly, and yearly rollups</span>
  </div>
  <div class="table-responsive">
    <table class="table align-middle mb-0">
      <thead>
        <tr>
          <th>Period</th>
          <th>Location</th>
          <th>Game</th>
          <th class="text-end">Machines</th>
          <th class="text-end">Cash In</th>
          <th class="text-end">Cash Out</th>
          <th class="text-end">Gross</th>
          <th class="text-end">Coin In</th>
          <th class="text-end">Coin Out</th>
          <th class="text-end">Games Played</th>
          <th class="text-end">Games Won</th>
          <th class="text-end">Reads</th>
          <th>Drill-down</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngIf="!loading && rows.length === 0">
          <td colspan="13" class="text-center text-muted py-4">No stats found for the current filters.</td>
        </tr>
        <tr *ngFor="let row of rows; trackBy: trackByRow" class="clickable-row" [class.table-active]="selectedRow?.key === row.key" (click)="selectRow(row)">
          <td>{{ row.periodLabel }}</td>
          <td class="fw-semibold">{{ row.groupLabel }}</td>
          <td>{{ row.gameLabel }}</td>
          <td class="text-end">{{ row.boardCount }}</td>
          <td class="text-end">{{ row.cashIn | number:'1.0-2' }}</td>
          <td class="text-end">{{ row.cashOut | number:'1.0-2' }}</td>
          <td class="text-end fw-semibold">{{ row.gross | number:'1.0-2' }}</td>
          <td class="text-end">{{ row.coinIn | number:'1.0-2' }}</td>
          <td class="text-end">{{ row.coinOut | number:'1.0-2' }}</td>
          <td class="text-end">{{ row.gamesPlayed | number }}</td>
          <td class="text-end">{{ row.gamesWon | number }}</td>
          <td class="text-end">{{ row.reads | number }}</td>
          <td><button type="button" class="btn btn-sm btn-outline-primary" (click)="$event.stopPropagation(); selectRow(row)">View machines</button></td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<div #detailsSection class="card shadow-sm mt-4" *ngIf="selectedRow">
  <div class="card-header d-flex justify-content-between align-items-center">
    <div>
      <div class="eyebrow text-uppercase">Machine detail</div>
      <h5 class="mb-0">{{ selectedRow.groupLabel }} • {{ selectedRow.periodLabel }}</h5>
    </div>
    <div class="d-flex gap-2">
      <span class="text-muted small align-self-center">Showing {{ detailRows.length }} machine rows for the selected result</span>
      <button type="button" class="btn btn-outline-secondary btn-sm" (click)="exportDetailsCsv()">Export details</button>
    </div>
  </div>
  <div class="table-responsive">
    <table class="table align-middle mb-0">
      <thead>
        <tr>
          <th>Period</th>
          <th>Location</th>
          <th>Machine</th>
          <th>Game</th>
          <th class="text-end">Cash In</th>
          <th class="text-end">Cash Out</th>
          <th class="text-end">Gross</th>
          <th class="text-end">Coin In</th>
          <th class="text-end">Coin Out</th>
          <th class="text-end">Games Played</th>
          <th class="text-end">Games Won</th>
          <th class="text-end">Reads</th>
          <th>Sales Rep</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngIf="detailRows.length === 0">
          <td colspan="13" class="text-center text-muted py-4">No machine-level data available for this selection.</td>
        </tr>
        <tr *ngFor="let item of detailRows; trackBy: trackByDetail">
          <td>{{ item.lastStatDate }}</td>
          <td>{{ item.locationName }}</td>
          <td class="fw-semibold">{{ item.boardLabel }}</td>
          <td>
            <div class="fw-semibold">{{ item.gameName }}</div>
            <div class="text-muted small" *ngIf="item.gameVersion">{{ item.gameVersion }}</div>
          </td>
          <td class="text-end">{{ item.cashIn | number:'1.0-2' }}</td>
          <td class="text-end">{{ item.cashOut | number:'1.0-2' }}</td>
          <td class="text-end fw-semibold">{{ item.gross | number:'1.0-2' }}</td>
          <td class="text-end">{{ item.coinIn | number:'1.0-2' }}</td>
          <td class="text-end">{{ item.coinOut | number:'1.0-2' }}</td>
          <td class="text-end">{{ item.gamesPlayed | number }}</td>
          <td class="text-end">{{ item.gamesWon | number }}</td>
          <td class="text-end">{{ item.reads | number }}</td>
          <td>{{ item.salesRepName }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>
